import {cookieExceptionPages, cookieUnblockedProviders} from "./cookieBannerConfig";

// ID des Containers, in den das DCcookie-Plugin (plugins/DCcookie) das Start-Banner rendert
const BANNER_ID = 'cookieMainPage';

// Kennzeichnet die von uns gestarteten Skripte, damit sie im DOM erkennbar sind
const UNBLOCKED_ATTRIBUTE = 'data-dccookie-unblocked';

// Markiert Container, in denen der Anbieter schon einmal Inhalt gerendert hat
const RENDERED_ATTRIBUTE = 'data-dccookie-rendered';

// Gestartete Skripte je Anbieter, um ein Formular bei Bedarf neu einbinden zu können
const startedScripts = new Map();

/**
 * Wendet auf den in cookieBannerConfig gepflegten Seiten zwei Sonderregeln an:
 * das Cookie-Banner wird entfernt und die dort freigegebenen Anbieter werden
 * ohne Einwilligung gestartet.
 *
 * Beides muss wiederholt laufen, weil das DCcookie-Plugin seine Elemente erst
 * nach dem Laden der Seite einhängt. Beide Schritte sind idempotent.
 */
export function initCookieBanner() {
    if (!isExceptionPage()) return;

    applyExceptions();

    // Der Observer bleibt aktiv: der Consent-Manager hängt seine Elemente nicht nur
    // beim Laden ein, sondern auch später, wenn der Besucher die Einstellungen im
    // Footer benutzt. Die Prüfungen sind billig und beenden sich sofort wieder.
    const observer = new MutationObserver(function () {
        applyExceptions();
    });
    observer.observe(document.documentElement, {childList: true, subtree: true});
}

function applyExceptions() {
    removeBanner();
    unblockProviders();
}

/**
 * Entfernt das Start-Banner.
 *
 * Das Element wird bewusst entfernt und nicht nur per CSS versteckt: ein noch
 * vorhandenes `.dcCookieModal.open` würde über `body:has(...)` das Scrollen der
 * Seite sperren (siehe _dccookie.scss im dcLegacyApp).
 */
function removeBanner() {
    const banner = document.getElementById(BANNER_ID);
    if (!banner) return;

    banner.remove();
}

function unblockProviders() {
    cookieUnblockedProviders.forEach(function (entry) {
        const provider = findProvider(entry.provider);
        if (!provider) return;

        unblockProvider(provider, entry.keepBlocked || []);
    });
}

function findProvider(id) {
    const dcCookie = (window.GLOBALS || {}).dcCookie || {};
    const providers = Array.isArray(dcCookie.providers) ? dcCookie.providers : [];
    const wanted = String(id).toLowerCase();

    return providers.find(function (provider) {
        return String(provider.id).toLowerCase() === wanted;
    }) || null;
}

function unblockProvider(provider, keepBlocked) {
    const containers = getExternalMediaContainers(provider);
    const blockedScripts = getBlockedScripts(provider, keepBlocked);

    if (!blockedScripts.length) {
        rerenderIfCleared(provider, containers);
        return;
    }

    // Solange der Einwilligungs-Hinweis noch nicht im Container steht, ist das Plugin
    // noch nicht fertig. Erst danach aufräumen, sonst rendert es den Hinweis über das
    // bereits eingebundene Formular.
    if (containers.length && !containers.some(hasContent)) return;

    clearContainers(containers);
    blockedScripts.forEach(function (element) {
        rememberScript(provider, element);
        runScript(element);
    });
}

/**
 * Bindet ein Formular neu ein, wenn sein Container nachträglich geleert wurde.
 *
 * Das passiert, wenn der Besucher auf einer dieser Seiten später über die
 * Footer-Einstellungen zustimmt: der Consent-Manager leert dann alle
 * Externe-Medien-Container des Anbieters — und damit auch das bereits
 * eingebundene Formular.
 *
 * Ein leerer Container allein reicht dafür nicht: solange das Formular noch lädt,
 * ist er ebenfalls leer. Deshalb wird jeder Container markiert, sobald er das erste
 * Mal Inhalt hat, und nur ein markierter, wieder leerer Container gilt als geleert.
 * Die Markierung wird beim Neueinbinden entfernt und mit dem neuen Inhalt wieder
 * gesetzt.
 */
function rerenderIfCleared(provider, containers) {
    const scripts = startedScripts.get(provider.id);
    if (!scripts || !scripts.length) return;

    containers.filter(hasContent).forEach(function (container) {
        container.setAttribute(RENDERED_ATTRIBUTE, '1');
    });

    const cleared = containers.filter(function (container) {
        return container.hasAttribute(RENDERED_ATTRIBUTE) && !hasContent(container);
    });
    if (!cleared.length) return;

    cleared.forEach(function (container) {
        container.removeAttribute(RENDERED_ATTRIBUTE);
    });
    scripts.forEach(function (script) {
        insertScript(script, null);
    });
}

function rememberScript(provider, element) {
    const scripts = startedScripts.get(provider.id) || [];

    scripts.push(toScriptDefinition(element));
    startedScripts.set(provider.id, scripts);
}

function toScriptDefinition(element) {
    return element.src
        ? {src: element.src, code: ''}
        : {src: '', code: element.innerHTML};
}

function clearContainers(containers) {
    containers.forEach(function (container) {
        container.innerHTML = '';
    });
}

function getBlockedScripts(provider, keepBlocked) {
    if (!provider.scriptclass) return [];

    return Array.from(document.getElementsByClassName(provider.scriptclass)).filter(function (element) {
        if (element.tagName !== 'SCRIPT') return false;

        return !keepBlocked.some(function (selector) {
            return element.matches(selector);
        });
    });
}

function getExternalMediaContainers(provider) {
    if (!provider.externalMediaContainerClass) return [];

    return Array.from(document.getElementsByClassName(provider.externalMediaContainerClass));
}

function hasContent(element) {
    return element.innerHTML.trim() !== '';
}

/**
 * Startet ein blockiertes Skript: das Original ist mit type="text/plain" inaktiv und
 * wird durch eine lauffähige Kopie ersetzt.
 *
 * Das Original wird anschließend entfernt, damit der Consent-Manager es bei einer
 * späteren Einwilligung nicht ein zweites Mal startet — sonst stünde das Formular
 * doppelt auf der Seite.
 */
function runScript(element) {
    insertScript(toScriptDefinition(element), element);
    element.remove();
}

function insertScript(definition, reference) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.setAttribute(UNBLOCKED_ATTRIBUTE, '1');

    if (definition.src) {
        script.src = definition.src;
    } else {
        script.text = definition.code;
    }

    if (reference && reference.parentNode) {
        reference.parentNode.insertBefore(script, reference);
    } else {
        document.body.appendChild(script);
    }
}

function isExceptionPage() {
    const currentPath = normalizePath(window.location.pathname);

    return cookieExceptionPages.some(function (page) {
        return normalizePath(toPath(page)) === currentPath;
    });
}

/**
 * Nimmt sowohl Pfade als auch vollständige URLs entgegen und liefert den Pfad.
 */
function toPath(page) {
    const value = String(page).trim();
    if (!/^https?:\/\//i.test(value)) return value;

    try {
        return new URL(value).pathname;
    } catch (e) {
        return value;
    }
}

/**
 * Vereinheitlicht einen Pfad: ohne Query/Anker, klein geschrieben, ohne abschließenden "/".
 */
function normalizePath(path) {
    const withoutQuery = String(path).split(/[?#]/)[0];
    const withoutTrailingSlash = withoutQuery.replace(/\/+$/, '');

    return (withoutTrailingSlash || '/').toLowerCase();
}
